import type { AutomationProjectDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, GitBranch, Play, RefreshCw, Settings } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  ErrorState,
  SkeletonRows,
} from '../../../components/ui';
import type { MessageKey } from '../../../i18n';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { useAutomationCan, useAutomationProject } from '../hooks';
import { Mark, ProjectNotice } from '../ui';
import { ClassesTab } from './ClassesTab';
import { DashboardTab } from './DashboardTab';
import { ProjectSettingsDialog } from './ProjectSettingsDialog';
import { ReadinessMatrix } from './ReadinessMatrix';
import { ScanRepoDialog } from './ScanRepoDialog';
import { SuitesTab } from './SuitesTab';
import { TestDataTab } from './TestDataTab';

const TABS = ['dashboard', 'suites', 'classes', 'data', 'readiness'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, MessageKey> = {
  dashboard: 'automation.project.tab.dashboard',
  suites: 'automation.project.tab.suites',
  classes: 'automation.project.tab.classes',
  data: 'automation.project.tab.data',
  readiness: 'automation.project.tab.readiness',
};

const asTab = (tab: string): Tab =>
  (TABS as readonly string[]).includes(tab) ? (tab as Tab) : 'dashboard';

const ProjectView = ({
  teamId,
  project,
  tab,
}: {
  teamId: string;
  project: AutomationProjectDto;
  tab: Tab;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const can = useAutomationCan();
  const [scanOpen, setScanOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Mark label={project.key} color={project.color} size="lg" />
            {project.name}
          </span>
        }
        description={
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-ink-subtle">
              <span className="inline-flex items-center gap-1">
                <GitBranch className="size-3.5" aria-hidden="true" />
                {t('automation.project.team.gitlabProject', { id: project.gitlabProjectId })}
              </span>
              <span>{t('automation.projectTests', { count: project.testCount })}</span>
              <Badge>{project.testType}</Badge>
              {project.sourceProject ? <span>{t('automation.project.repoConnected')}</span> : null}
            </span>
            {project.description ? <span>{project.description}</span> : null}
          </span>
        }
        actions={
          <div className="flex flex-wrap justify-end gap-2">
            <Link
              to="/automation/teams/$teamId"
              params={{ teamId }}
              className={buttonClassName('secondary')}
            >
              <ArrowLeft className="size-3.5" />
              {t('automation.project.back')}
            </Link>
            {can.run ? (
              <Button onClick={() => setScanOpen(true)}>
                <RefreshCw className="size-3.5" />
                {t('automation.project.scan')}
              </Button>
            ) : null}
            {project.gitlabPipelineUrl ? (
              <a
                href={project.gitlabPipelineUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonClassName('secondary')}
              >
                <ExternalLink className="size-3.5" />
                GitLab
              </a>
            ) : null}
            {can.manage ? (
              <Button
                aria-label={t('automation.project.settings.title')}
                title={t('automation.project.settings.title')}
                onClick={() => setSettingsOpen(true)}
              >
                <Settings className="size-4" />
              </Button>
            ) : null}
            {can.run ? (
              <Button
                variant="primary"
                onClick={() =>
                  void navigate({ to: '/automation/runs/new', search: { project: project.id } })
                }
              >
                <Play className="size-3.5" />
                {t('automation.project.run')}
              </Button>
            ) : null}
          </div>
        }
      />

      <ProjectNotice note={project.note} />

      <nav
        aria-label={t('automation.project.tabs')}
        className="flex gap-1 overflow-x-auto border-b border-border"
      >
        {TABS.map((key) => (
          <Link
            key={key}
            to="/automation/teams/$teamId/projects/$projectId"
            params={{ teamId, projectId: project.id }}
            search={key === 'dashboard' ? {} : { tab: key }}
            aria-current={tab === key ? 'page' : undefined}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
              tab === key
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {t(TAB_LABELS[key])}
          </Link>
        ))}
      </nav>

      {tab === 'dashboard' ? <DashboardTab projectId={project.id} /> : null}
      {tab === 'suites' ? <SuitesTab projectId={project.id} /> : null}
      {tab === 'classes' ? <ClassesTab projectId={project.id} /> : null}
      {tab === 'data' ? <TestDataTab projectId={project.id} /> : null}
      {tab === 'readiness' ? <ReadinessMatrix projectId={project.id} /> : null}

      {scanOpen ? <ScanRepoDialog project={project} onOpenChange={setScanOpen} /> : null}
      {settingsOpen ? (
        <ProjectSettingsDialog project={project} onOpenChange={setSettingsOpen} />
      ) : null}
    </>
  );
};

/**
 * A project: dashboard, test suites, test classes, test data and readiness
 * (tabs via `?tab=`), with the repository scan and, for admins, the settings.
 */
export function ProjectPage({
  teamId,
  projectId,
  tab,
}: {
  teamId: string;
  projectId: string;
  tab: string;
}) {
  const project = useAutomationProject(projectId);

  // data first: after deleting, the refetch fails while the page navigates away
  if (project.data) return <ProjectView teamId={teamId} project={project.data} tab={asTab(tab)} />;
  if (project.error) {
    return (
      <Card>
        <ErrorState error={project.error} onRetry={() => void project.refetch()} />
      </Card>
    );
  }
  return <SkeletonRows rows={4} className="p-0" />;
}
